import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import {
  FileText,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  FolderOpen,
} from "lucide-react";
import { Badge } from "../components/ui/badge";

interface EmployeeDocuments {
  offerLetter?: string | null;
  contractLetter?: string | null;
  nda?: string | null;
  aadharCard?: string | null;
  contract?: string | null;
  panCard?: string | null;
}

interface EmployeeData {
  fullName: string;
  email: string;
  employeeType: "Employee" | "Intern" | "Part-Time";
  documents?: EmployeeDocuments;
}

interface DocumentInfo {
  key: keyof EmployeeDocuments;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const documentLabels: Record<
  keyof EmployeeDocuments,
  { label: string; description: string }
> = {
  offerLetter: {
    label: "Offer Letter",
    description: "Your official offer letter from the company",
  },
  contractLetter: {
    label: "Contract Letter",
    description: "Employment contract agreement",
  },
  nda: {
    label: "Non-Disclosure Agreement (NDA)",
    description: "Confidentiality and non-disclosure agreement",
  },
  aadharCard: {
    label: "Aadhar Card",
    description: "Government issued identity proof",
  },
  contract: {
    label: "Contract Agreement",
    description: "Service contract agreement",
  },
  panCard: {
    label: "PAN Card",
    description: "Permanent Account Number card",
  },
};

export default function EmployeeDocuments() {
  const [employeeData, setEmployeeData] = useState<EmployeeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingDoc, setDownloadingDoc] = useState<string | null>(null);

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  const fetchEmployeeData = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees/profile/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEmployeeData(response.data);
    } catch (error: any) {
      console.error("Error fetching employee data:", error);
      setError(error.response?.data?.message || "Failed to fetch documents");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDocument = (documentUrl: string) => {
    window.open(documentUrl, "_blank");
  };

  const handleDownloadDocument = async (
    documentUrl: string,
    documentName: string
  ) => {
    try {
      setDownloadingDoc(documentName);

      // Create a temporary link and trigger download
      const link = document.createElement("a");
      link.href = documentUrl;
      link.download = documentName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading document:", error);
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Get only uploaded documents
  const getUploadedDocuments = (): DocumentInfo[] => {
    if (!employeeData?.documents) return [];

    const uploadedDocs: DocumentInfo[] = [];

    Object.entries(employeeData.documents).forEach(([key, url]) => {
      if (url) {
        const docKey = key as keyof EmployeeDocuments;
        const docInfo = documentLabels[docKey];
        if (docInfo) {
          uploadedDocs.push({
            key: docKey,
            label: docInfo.label,
            description: docInfo.description,
            icon: <FileText className="h-5 w-5" />,
          });
        }
      }
    });

    return uploadedDocs;
  };

  const uploadedDocuments = getUploadedDocuments();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">
            Loading your documents...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Documents</h1>
          <p className="text-muted-foreground mt-1">
            View and download your onboarding documents
          </p>
        </div>
      </div>

      {/* Summary Card */}
      <div className="mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Document Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">
                    {uploadedDocuments.length}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Document{uploadedDocuments.length !== 1 ? "s" : ""}{" "}
                    Available
                  </p>
                </div>
              </div>
              <div className="h-12 w-px bg-border" />
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                  {employeeData?.employeeType || "Employee"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Documents Grid */}
      {uploadedDocuments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {uploadedDocuments.map((doc) => {
            const documentUrl = employeeData?.documents?.[doc.key];

            return (
              <Card key={doc.key} className="transition-all hover:shadow-lg">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                        {doc.icon}
                      </div>
                      <div>
                        <CardTitle className="text-base">{doc.label}</CardTitle>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      <CheckCircle className="mr-1 h-3 w-3" />
                      Available
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {doc.description}
                  </p>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() =>
                        documentUrl && handleViewDocument(documentUrl)
                      }
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() =>
                        documentUrl &&
                        handleDownloadDocument(documentUrl, doc.label)
                      }
                      disabled={downloadingDoc === doc.label}
                    >
                      {downloadingDoc === doc.label ? (
                        <>
                          <Spinner
                            variant="ellipsis"
                            size={16}
                            className="mr-2"
                          />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <FolderOpen className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">
                No Documents Available
              </h3>
              <p className="text-muted-foreground mb-4">
                You don't have any documents uploaded yet.
              </p>
              <p className="text-sm text-muted-foreground">
                Please contact admin if you need to upload or access your
                documents.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
